import { Clear, ExpandMoreOutlined } from "@mui/icons-material";
import { IconButton, styled, useTheme } from "@mui/material";
import type { SxProps, TextFieldProps, Theme } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import { usePlacesWidget } from "react-google-autocomplete";
import TextField from "@mui/material/TextField";

import { useEffect, useState } from "react";
import type { ChangeEvent, MouseEvent } from "react";
import { GoogleApiKey } from "../../../common/constants";

// ==============================================================
// Types
// ==============================================================

interface AddressComponent {
    types: string[];
    long_name: string;
    short_name: string;
}

interface PlacesResult {
    address_components: Record<string, AddressComponent>;
}

interface AddressResponse {
    address1: string;
    zipcode: string;
    state: string;
    city: string;
    address2?: string;
}

interface TableDropdownOption {
    name: string;
    value: string;
}

interface TableDropdownProps {
    size: string;
    currentValue: string;
    options: TableDropdownOption[];
    onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    sx?: SxProps<Theme>;
    label?: string;
}

interface AddressLookupProps extends Omit<TextFieldProps, "onChange" | "onClick"> {
    sendResponse: (response: AddressResponse) => void;
    currentValue: string;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    // Optional: the login/register address field renders without an onClick; the clear button
    // (which is the only caller of updateClick) only appears when currentValue is set.
    onClick?: (event: MouseEvent) => void;
    lookupOn: boolean;
    resetDirty?: boolean;
}

interface AccountInputBoxProps extends Omit<TextFieldProps, "onChange" | "onClick"> {
    currentValue?: string;
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
    onClick?: (event: MouseEvent) => void;
}

// ==============================================================

const TF = styled(TextField)(({ theme, sx = {} }) => ({
    ...(sx as Record<string, string>),

    "& .Mui-focused fieldset,& label.Mui-focused": {
        borderColor: `${theme.palette.account.accent} !important`,
        color: theme.palette.account.accent,
    },
    ".MuiSelect-select, input": {
        padding: `10px 14px`,
        minHeight: `unset`,
        overflow: `unset`,
        fontWeight: 600,
        boxShadow: `0 0 0 50px white inset`,
    },

    ".MuiInput-root::before, .MuiInput-root:hover::before, .MuiInputBase-root::after": {
        borderBottom: `2px solid ${theme.palette.account.accent}`,
    },
    svg: {
        color: theme.palette.account.accent,
    },
    label: {
        lineHeight: `unset`,
        top: `-2px`,
        "&.MuiInputLabel-shrink": {
            top: `2px`,
        },
    },
    fieldset: {
        borderRadius: `8px`,
        overflow: `unset`,
    },
}));

export const TableDropdown = ({ size, currentValue, options, onChange, sx, label = `` }: TableDropdownProps) => {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    return (
        <TF
            label={label}
            onClick={() => setOpen(!open)}
            select
            sx={
                {
                    mb: size === `small` ? 0.8 : 1,
                    verticalAlign: `middle`,
                    "& .MuiSelect-select": {
                        padding: `4px`,
                        fontSize: 15,
                        whiteSpace: `nowrap !important`,
                        textOverflow: `clip !important`,
                        minHeight: `unset !important`,
                    },
                    ".MuiInput-input:focus, .MuiInput-input": {
                        backgroundColor: `transparent`,
                    },
                    ...(sx as Record<string, unknown>),
                } as SxProps<Theme>
            }
            variant="standard"
            className="select shopping-year-select"
            SelectProps={{
                open,
                endAdornment: <ExpandMoreOutlined sx={{ cursor: `pointer` }} onClick={() => setOpen(!open)} />,
                MenuProps: {
                    disableScrollLock: true,
                    PaperProps: {
                        sx: {
                            boxShadow: `rgba(0, 0, 0, 0.24) 0px 3px 5px`,
                            ".MuiButtonBase-root": {
                                fontWeight: 600,
                                fontSize: 15,

                                "&.Mui-selected": {
                                    background: `${theme.palette.account.accent}10 !important`,
                                },
                            },
                        },
                    },
                },
            }}
            value={currentValue || options?.[0]?.value || ``}
            onChange={onChange}
        >
            {options.map(({ name, value }) => (
                <MenuItem key={value} value={value}>
                    {name}
                </MenuItem>
            ))}
        </TF>
    );
};

export const AddressLookup = ({
    sendResponse,
    currentValue,
    onChange,
    onClick,
    label,
    InputProps,
    lookupOn,
    resetDirty,
    sx,
    ...rest
}: AddressLookupProps) => {
    const [dirty, setDirty] = useState(false);

    const setResponse = (v: PlacesResult): void => {
        const ac = v.address_components;
        const re: AddressResponse = { address1: ``, zipcode: ``, state: ``, city: `` };
        Object.keys(ac).forEach(key => {
            const component = ac[key];
            const componentType = component.types[0];

            switch (componentType) {
                case `street_number`: {
                    re.address1 = `${component.long_name} ${re.address1}`;
                    break;
                }
                case `route`: {
                    re.address1 += component.short_name;
                    break;
                }
                case `postal_code`: {
                    re.zipcode = `${component.long_name}${re.zipcode}`;
                    break;
                }

                case `subpremise`: {
                    re.address2 = component.long_name;
                    break;
                }

                case `locality`:
                    re.city = component.long_name;
                    break;
                case `administrative_area_level_1`: {
                    re.state = component.short_name;
                    break;
                }

                default:
            }
        });
        sendResponse(re);
    };

    const { ref } = usePlacesWidget({
        // autocompleteRef is reference to autocomplete object
        options: {
            componentRestrictions: { country: [`us`] },
            fields: [`address_components`],
            types: [`address`],
        },
        apiKey: GoogleApiKey,
        onPlaceSelected: setResponse,
        inputAutocompleteValue: `shipping address`,
    });

    useEffect(() => {
        const hideLookup = () =>
            setTimeout(() => {
                const elem = document.querySelectorAll(`.pac-container.pac-logo`);
                if (!elem?.length) {
                    return hideLookup();
                }
                elem.forEach(a => (a as HTMLElement).style.setProperty(`visibility`, `hidden`));
            }, 100);
        hideLookup();
    }, []);

    useEffect(() => {
        const elem = document.querySelector(`.pac-container.pac-logo`) as HTMLElement | null;
        if (lookupOn && elem) {
            elem.style.setProperty(`visibility`, `visible`);
        } else if (!lookupOn && elem) {
            elem.style.setProperty(`visibility`, `hidden`);
        }
    }, [lookupOn]);

    const updateValue = (v: ChangeEvent<HTMLInputElement>) => {
        if (!dirty && v.target.value) {
            setDirty(true);
        }
        if (!v.target.value && resetDirty && dirty) {
            setDirty(false);
        }
        onChange(v);
    };

    const updateClick = (v: MouseEvent) => {
        if (resetDirty && dirty) {
            setDirty(false);
        }
        // onClick is only wired up by callers that render the clear button; preserve the original
        // unguarded call (throws if a caller both omits onClick and triggers the clear button).
        onClick!(v);
    };

    return (
        <TF
            fullWidth
            sx={{
                maxWidth: `300px`,
                mb: 3,
                "& input::placeholder": {
                    color: `transparent`,
                },
                ...sx,
            }}
            value={currentValue || ``}
            InputProps={{
                endAdornment: currentValue ? (
                    <IconButton
                        tabIndex={-9999}
                        sx={{ position: `absolute`, opacity: `0.55 !important`, right: 0, top: `5px` }}
                        disabled={rest.disabled}
                        onClick={updateClick}
                    >
                        <Clear sx={{ ...(rest.disabled ? { color: `#777 !important` } : {}) }} />
                    </IconButton>
                ) : null,
                ...InputProps,
                inputRef: ref,
                autoComplete: dirty ? `new-password` : `off`,
            }}
            label={label}
            onChange={updateValue}
            {...rest}
        />
    );
};

export const AccountInputBox = ({
    currentValue,
    onChange,
    onClick,
    label,
    InputProps,
    sx,
    ...rest
}: AccountInputBoxProps) => (
    <TF
        fullWidth
        sx={{
            maxWidth: `300px`,
            mb: 3,
            "& .MuiInputBase-root": {
                paddingRight: 0,
            },

            ...sx,
        }}
        value={currentValue || ``}
        InputProps={{
            endAdornment: currentValue ? (
                <IconButton
                    tabIndex={-9999}
                    sx={{ position: `absolute`, right: 0 }}
                    disabled={rest.disabled}
                    onClick={onClick}
                >
                    <Clear sx={{ opacity: 0.55, ...(rest.disabled ? { color: `#777 !important` } : {}) }} />
                </IconButton>
            ) : null,
            ...InputProps,
        }}
        label={label}
        onChange={onChange}
        {...rest}
    />
);
